import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IEmailSent } from '../../interfaces/email-sent.interface';

/**
 * Servicio de vista para gestión de estado de comunicaciones
 * Maneja la selección de emails, visualización de dialogs, y cálculos de tracking
 */
@Injectable({
  providedIn: 'root'
})
export class CommunicationService {
  
  // ===== PROPIEDADES DE ESTADO =====
  
  /** Email seleccionado actualmente */
  selectedEmail: IEmailSent | null = null;
  
  /** Mostrar dialog de detalle */
  showDetail = false;
  
  /** Mostrar dialog de preview HTML */
  showPreview = false;
  
  /** Mostrar dialog de tracking timeline */
  showTracking = false;
  
  /** HTML para preview */
  previewHtml = '';
  
  /** Eventos de tracking construidos */
  trackingEvents: any[] = [];

  constructor(private sanitizer: DomSanitizer) {}

  // ===== MÉTODOS DE CÁLCULO =====

  /**
   * Genera el display de engagement para un email
   * @param email - Email a procesar
   * @returns String formateado con iconos (ej: "📖 2 aperturas | 🔗 1 click")
   */
  getEngagementDisplay(email: IEmailSent): string {
    const opens = email.openCount || 0;
    const clicks = email.clickCount || 0;
    
    const parts = [];
    
    if (opens > 0) {
      parts.push(`📖 ${opens} apertura${opens > 1 ? 's' : ''}`);
    }
    
    if (clicks > 0) {
      parts.push(`🔗 ${clicks} click${clicks > 1 ? 's' : ''}`);
    }
    
    return parts.length > 0 ? parts.join(' | ') : 'Sin interacción';
  }

  /**
   * Calcula el progreso de un email basado en su estado
   * @param status - Estado del email
   * @returns Porcentaje de progreso (0-100)
   */
  getStatusProgress(status: string): number {
    const progressMap: Record<string, number> = {
      'PENDING': 0,
      'SENT': 25,
      'DELIVERED': 50,
      'OPENED': 75,
      'CLICKED': 100,
      'BOUNCED': 0,
      'FAILED': 0
    };
    
    return progressMap[status] || 0;
  }

  /**
   * Construye el timeline de eventos de tracking de un email
   * @param email - Email con datos de tracking
   * @returns Array de eventos ordenados cronológicamente
   */
  buildTrackingTimeline(email: IEmailSent): any[] {
    const events: any[] = [];
    
    // Evento: SENT
    if (email.sentAt) {
      events.push({
        type: 'SENT',
        timestamp: email.sentAt,
        label: 'Enviado',
        icon: 'fa-solid fa-paper-plane',
        color: '#42A5F5'
      });
    }
    
    // Evento: DELIVERED
    if (email.deliveredAt) {
      events.push({
        type: 'DELIVERED',
        timestamp: email.deliveredAt,
        label: 'Entregado',
        icon: 'fa-solid fa-check',
        color: '#66BB6A'
      });
    }
    
    // Evento: OPENED
    if (email.openedAt) {
      events.push({
        type: 'OPENED',
        timestamp: email.openedAt,
        label: 'Abierto',
        icon: 'fa-solid fa-envelope-open',
        color: '#26A69A',
        count: email.openCount || 1
      });
    }
    
    // Evento: CLICKED
    if (email.clickedAt) {
      events.push({
        type: 'CLICKED',
        timestamp: email.clickedAt,
        label: 'Click en enlace',
        icon: 'fa-solid fa-hand-pointer',
        color: '#9C27B0',
        count: email.clickCount || 1
      });
    }
    
    // Evento: BOUNCED
    if (email.bouncedAt) {
      events.push({
        type: 'BOUNCED',
        timestamp: email.bouncedAt,
        label: 'Rebotado',
        icon: 'fa-solid fa-exclamation-triangle',
        color: '#FF7043',
        reason: email.bounceReason
      });
    }
    
    // Evento: FAILED
    if (email.failedAt) {
      events.push({
        type: 'FAILED',
        timestamp: email.failedAt,
        label: 'Fallido',
        icon: 'fa-solid fa-times-circle',
        color: '#EF5350',
        error: email.errorMessage || email.error?.message
      });
    }
    
    // Ordenar eventos por timestamp (más antiguos primero)
    return events.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateA - dateB;
    });
  }

  /**
   * Sanitiza HTML para prevenir XSS
   * @param html - HTML sin sanitizar
   * @returns HTML sanitizado
   */
  sanitizeHtml(html: string): SafeHtml {
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html);
    return sanitized || '';
  }

  /**
   * Sanitiza HTML de forma permisiva (para preview)
   * PRECAUCIÓN: Solo usar con contenido confiable
   * @param html - HTML a renderizar
   * @returns HTML marcado como seguro
   */
  bypassSecurityTrustHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
